import { useCallback, useEffect, useRef, useState } from "react";

const BUBBLE_SIZE = 48;
const EDGE_PAD = 8;

type SavedPos = { x: number; y: number };

function readSavedPos(storageKey: string): SavedPos | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedPos;
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function readSafeTop(): number {
  if (typeof document === "undefined") return 28;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--native-safe-top")
    .trim();
  const parsed = parseFloat(raw);
  if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 47 : 28;
}

function readSafeBottom(): number {
  if (typeof document === "undefined") return 0;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--native-safe-bottom")
    .trim();
  const parsed = parseFloat(raw);
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : 0;
}

function minTopY(): number {
  return readSafeTop() + EDGE_PAD;
}

function clampPos(x: number, y: number): SavedPos {
  const minY = minTopY();
  const maxX = Math.max(EDGE_PAD, window.innerWidth - BUBBLE_SIZE - EDGE_PAD);
  const maxY = Math.max(
    minY,
    window.innerHeight - BUBBLE_SIZE - EDGE_PAD - readSafeBottom(),
  );
  return {
    x: Math.min(Math.max(x, EDGE_PAD), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  };
}

function defaultTopRight(): SavedPos {
  if (typeof window === "undefined") {
    return { x: EDGE_PAD, y: minTopY() };
  }
  return clampPos(
    window.innerWidth - BUBBLE_SIZE - EDGE_PAD - 4,
    minTopY(),
  );
}

/** Draggable floating bubble position (profile chip, guest settings, etc.). */
export function useNativeFloatingBubble(storageKey: string) {
  const [pos, setPos] = useState<SavedPos | null>(() => {
    const saved = readSavedPos(storageKey);
    return saved ? clampPos(saved.x, saved.y) : null;
  });
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const onResize = () => {
      setPos((prev) => (prev ? clampPos(prev.x, prev.y) : prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const origin = pos ?? defaultTopRight();

      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: origin.x,
        originY: origin.y,
        moved: false,
      };

      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pos],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        drag.moved = true;
      }

      setPos(clampPos(drag.originX + dx, drag.originY + dy));
    },
    [],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;

      if (drag.moved) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }

      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }

      dragRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!pos) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(pos));
    } catch {
      // ignore
    }
  }, [pos, storageKey]);

  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto" as const }
    : undefined;

  return {
    style,
    suppressClickRef,
    pointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
    },
  };
}
